import type { Styles } from '@adornis/ass/style.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BHPromptWorkshopAppointmentsView } from '@adornis/bh-registration/client/bh-prompt-appointments-view.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ChemistryLiteElement } from '@adornis/chemistry/chemistry-lite-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { useContext } from '@adornis/functional-lit/directive-provide-context.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { getCurrentComponent, stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { createContext, provide } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import {
  chooseValueByLang,
  getProductTags,
  renderDateTimeBoxPair,
  renderProductCardTags,
  timeSlotTime,
  type ProductCardData,
} from './productCardShared.js';

export type WorkshopCardData = ProductCardData & {
  workshop: NarrowedEntity<
    Workshop,
    {
      _id: 1;
      isLDAPOnly: 1;
      hasPrice: 1;
      prices: { unitAmount: 1 };
      timeframe: { name: 1 };
      presenceType: 1;
      language: 1;
      topic: 1;
      name: 1;
      translatedDescriptionHTML: { langKey: 1; text: 1 };
      capacity: 1;
      leftCapacity: 1;
      hideCapacity: 1;
      start: 1;
      end: 1;
      productEntryRequirementLabels: 1;
      appointments: {
        start: 1;
        end: 1;
      };
      settings: { courseLocation: 1; inheritedCourseLocation: 1 };
    }
  >;
  isTeaser: boolean;
  teaserDate?: DateTime;
  selected: boolean;
  bookedAlready: boolean;
  isClickable: boolean;
  isBlockedByOtherSelection: boolean;
  events?: {
    onShowDetailPage?: () => void;
  };
};

export const workshopCardDataContext = createContext<WorkshopCardData>('workshop-card-data');
const IsMobileViewEnabledContext = createContext<boolean>('is-mobile-view-enabled');

export const teaserBanner = stateful<{ isTeaser: boolean; teaserDate?: DateTime }>(({ isTeaser, teaserDate }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  const teaserBanner = (() => {
    if (!isTeaser || !theme) return nothing;

    const banner = content => html`
      <x-flex
        space="xxs"
        ${css({
          position: 'absolute',
          top: '8px',
          right: '-50px',
          width: '170px',
          height: 'auto',
          color: '#000',
          rotate: '45deg',
        })}
      >
        ${content}
      </x-flex>
    `;

    if (!teaserDate)
      return banner(html`
        <tr-text
          bold
          small
          ${css({
            padding: theme.spacing.xs,
            background: theme.colors.tone.onyx,
            color: '#fff',
            textAlign: 'center',
          })}
        >
          ${translate('soon')} <br /><tr-text ${css({ display: 'inline-block' })} bold
            >${translate('available')}!</tr-text
          >
        </tr-text>
      `);

    return banner(html`
      <tr-text
        bold
        small
        ${css({
          padding: theme.spacing.xs,
          background: theme.colors.tone.onyx,
          color: '#fff',
          textAlign: 'center',
          paddingTop: `calc(${theme.spacing.xs} + 5px)`,
          paddingBottom: `calc(${theme.spacing.xs} + 5px)`,
        })}
      >
        ${translate('available')} <br /><tr-text ${css({ display: 'inline-block' })} bold
          >${teaserDate.toFormat('dd.MM.yyyy')}!</tr-text
        >
      </tr-text>
    `);
  })();

  return html` ${teaserBanner} `;
});

@customElement('bh-workshop-card')
export class BHWorkshopCard extends ChemistryLitElement {
  @state()
  workshopCardData?: WorkshopCardData;

  @state() private readonly size = new ComponentSizeController(this);

  @provide({ context: IsMobileViewEnabledContext })
  @state()
  isMobileViewEnabled = true;

  @state() readonly translation = new TranslationController(this);

  override render() {
    this.isMobileViewEnabled = (this.size.width ?? 0) < 768;

    return html`
      <slot></slot> ${teaserBanner({
        isTeaser: this.workshopCardData?.isTeaser ?? false,
        teaserDate: this.workshopCardData?.teaserDate,
      })}
    `;
  }

  override styles(): Styles[] {
    const shadow = '0 1px 3px 0 #d4d4d5, 0 0 0 1px #d4d4d5';
    const selectedShadow = `0 1px 3px 0 ${this.workshopCardData?.colorDef.stepColor}, 0 0 0 1px ${this.workshopCardData?.colorDef.stepColor}`;
    const raisedShadow = '0 0 0 1px #d4d4d5, 0 2px 4px 0 rgba(34,36,38,.12), 0 2px 10px 0 rgba(34,36,38,.15)';
    const hoverShadow = '0 1px 3px 0 #bcbdbd, 0 0 0 1px #d4d4d5';
    const hoverStyle = { transform: 'translateY(-2px)', boxShadow: hoverShadow };

    return [
      ...super.styles(),
      {
        ':host': {
          display: 'flex',
          borderRadius: this.sizes.borderRadius,
          boxShadow: this.workshopCardData?.selected ? selectedShadow : shadow,
          overflow: 'hidden',
          boxSizing: 'border-box',
          background: this.workshopCardData?.selected
            ? `rgb(from ${this.workshopCardData.colorDef.stepColor} r g b / 0.1)`
            : this.colors.white,
          position: 'relative',
          padding: '20px',
          userSelect: 'none',
          cursor: this.workshopCardData?.isClickable ? 'pointer' : 'not-allowed',
          '&[raised]': { boxShadow: raisedShadow },
          '&:hover': { '&[href]': hoverStyle },
          '@media print': { boxShadow: 'none', border: `1px solid ${this.colors.neutral.shade(200)}` },
        },
      },
    ];
  }
}

const workshopCardSelectionIndicator = stateful<WorkshopCardData>(
  ({ isBlockedByOtherSelection, bookedAlready, selected, colorDef, isClickable, isTeaser }) => {
    const theme = useObservable(() => DesignSystem.currentTheme, []);
    const translate = clientTranslator.translate.bind(clientTranslator);

    if (!theme) throw new Error('theme is undefined');

    if (isTeaser) return nothing;

    const content = (() => {
      if (bookedAlready)
        return html`
          <tr-text tone="subtle"> ${translate('REGISTRATION', 'appointmentAlreadyBooked')} </tr-text>
          <tr-tooltip .text=${translate('REGISTRATION', 'appointmentAlreadyBookedTooltip')}> </tr-tooltip>
        `;

      if (isBlockedByOtherSelection)
        return html`
          <tr-text tone="subtle"> ${translate('REGISTRATION', 'cannotSelectAppointmentCollision')} </tr-text>
          <tr-tooltip .text=${translate('REGISTRATION', 'cannotSelectAppointmentCollisionTooltip')}> </tr-tooltip>
        `;

      return html`
        <tr-h4>${selected ? '' : translate('select')}</tr-h4>
        <tr-icon
          huge
          ${css({
            color: selected ? colorDef.stepColor : 'black',
          })}
        >
          radio_button_unchecked
        </tr-icon>
      `;
    })();

    const wrapperCss = css({ cursor: !isClickable ? 'not-allowed' : 'pointer' });
    return html`<x-flex ${wrapperCss} horizontal crossaxis-center space="sm" ${css({ padding: theme.spacing.xs })}
      >${content}</x-flex
    >`;
  },
);

const workshopCardTerminButton = stateful<WorkshopCardData>(({ workshop, colorDef, start, end }) => {
  const translate = clientTranslator.translate.bind(clientTranslator);
  const showMobileView = useContext(IsMobileViewEnabledContext);
  const [maxAppointments, setMaxAppointments] = useState(2);

  if (!workshop.appointments || workshop.appointments.length === 0) return nothing as Renderable;

  const design = 'design_2' as string;

  if (design === 'design_1')
    return html`
      <tr-flex space="sm">
        ${timeSlotTime({ colorDef, start, end })}
        <tr-button
          mode="outline"
          ${css({
            borderColor: colorDef.moreProductsFontColor,
            color: colorDef.moreProductsFontColor,
            background: colorDef.moreProductsColor,
            zIndex: '10',
          })}
          @click=${event => {
            event.stopPropagation(); // necessary because parent also has click listener which should not get triggered
            return BHPromptWorkshopAppointmentsView.showPopup({
              props: {
                hasCloseButton: true,
                workshop,
                productColor: colorDef.productColor,
                productFontColor: colorDef.productFontColor,
              },
            });
          }}
        >
          ${(() => {
            if (workshop.appointments.length === 1 && workshop.appointments[0] && workshop.appointments[0].start) {
              const start = workshop.appointments[0].start.toLocaleString(DateTime.TIME_24_SIMPLE);
              const end = workshop.appointments[0].end!.toLocaleString(DateTime.TIME_24_SIMPLE);
              return `${start} - ${end} ${translate('uhr')}`;
            }
            return translate('termin', 'all_termins');
          })()}
        </tr-button>
      </tr-flex>
    `;

  const allAppointments = workshop.appointments.filter(appointment => appointment.start && appointment.end);
  const appointments = allAppointments.slice(0, maxAppointments);

  const showAllAppointmentsButton = (() => {
    if (allAppointments.length <= maxAppointments) return nothing;
    return html`<tr-flex
      space="xxs"
      @click=${() => setMaxAppointments(allAppointments.length)}
      ${css({
        cursor: 'pointer',
        width: '100%',
        background: colorDef.layoutLightContrastColor,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
        height: '20px',
        marginTop: '8px',
      })}
    >
      ${translate('termin', 'all_termins')}
    </tr-flex>`;
  })();

  if (design === 'design_2')
    return html`
      <tr-grid
        space="xxs"
        ${css({
          alignItems: 'stretch',
          gridTemplateColumns: showMobileView ? '1fr 1fr' : '1fr min-content',
        })}
      >
        ${appointments.map(appointment => {
          if (!appointment || !appointment.start || !appointment.end) return nothing;

          const date = appointment.start;
          const startTime = appointment.start.toLocaleString(DateTime.TIME_24_SIMPLE);
          const endTime = appointment.end.toLocaleString(DateTime.TIME_24_SIMPLE);
          const timeStackStyle = showMobileView ? 'horizontal' : 'vertical';

          return renderDateTimeBoxPair({ date, startTime, endTime, colorDef, timeStackStyle });
        })}
      </tr-grid>
      ${showAllAppointmentsButton}
    `;

  throw new Error('unknow design ' + design);
});

const workshopCardDescription = stateful<WorkshopCardData>(({ workshop }) => {
  return html`
    <bh-expandable-content
      @click=${event => {
        if (event.target.tagName === 'TR-LINK') {
          // do not select card when user clicked on Link
          event.stopPropagation();
        }
      }}
    >
      <tr-flex ${css({ gap: '1em' })}>
        ${chooseValueByLang(
          // TODO: fix types
          TextMultipleLangItem.mapAllTranslationsByLang(workshop.translatedDescriptionHTML as any),
        )}
      </tr-flex>
    </bh-expandable-content>
  `;
});

const workshopCardDetailPageButton = stateful<WorkshopCardData>(({ workshop, events, colorDef }) => {
  const translate = clientTranslator.translate.bind(clientTranslator);
  const host = getCurrentComponent().instance.part?.options?.host;

  return html`
    <tr-button
      mode="text"
      ${css({
        color: 'black',
        background: `rgb(from ${colorDef.stepColor} r g b / 0.3)`,
        padding: '4px 15px',
        borderRadius: '4px',
      })}
      @click=${e => {
        e.preventDefault();
        e.stopPropagation();
        if (!host) console.error('host is undefined');
        else
          (host as ChemistryLiteElement).dispatchEvent(
            new CustomEvent('onShowWorkshopDetail', { detail: { workshop }, composed: true, bubbles: true }),
          );
      }}
    >
      ${translate('REGISTRATION', 'show_detail_page')}
    </tr-button>
  `;
});

const workshopCardTags = stateful<WorkshopCardData>(({ workshop }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);
  const productLabels = useObservable(() => getProductTags(workshop), [workshop._id]);

  if (!theme) throw new Error('theme is undefined');
  if (!productLabels) return nothing;

  return renderProductCardTags(productLabels);
});

const workshopCapacityBar = stateful<WorkshopCardData>(({ workshop, colorDef }) => {
  const theme = useObservable(() => DesignSystem.currentTheme, []);
  const translate = clientTranslator.translate.bind(clientTranslator);

  if (!theme) throw new Error('theme is undefined');
  if (workshop.hideCapacity) return nothing;

  const bar = (progress: number, progressStr: string, progressColor: string) => html`
    <tr-flex
      flex
      center
      crossaxis-center
      ${css({
        position: 'relative',
        borderRadius: '6px',
        overflow: 'hidden',
        background: colorDef.layoutLightContrastColor,
      })}
    >
      <x-flex
        ${css({
          position: 'absolute',
          height: '100%',
          top: '0',
          left: '0',
          width: (progress * 100).toFixed(2) + '%',
          background: `rgb(from ${progressColor} r g b / 0.3)`,
          zIndex: '1',
        })}
      ></x-flex>
      <x-flex ${css({ zIndex: '10', padding: '3px 0' })}>
        <x-text> ${progressStr}</x-text>
      </x-flex>
    </tr-flex>
  `;

  const capacity = workshop.capacity;
  const takenSpots = workshop.capacity - Math.min(workshop.leftCapacity, workshop.capacity);
  if (takenSpots >= capacity) return bar(1, translate('REGISTRATION', 'queue_possible'), colorDef.warnColor);

  const progress = takenSpots / capacity;
  const progressStr = Math.min(takenSpots, capacity) + ' / ' + capacity;
  return bar(progress, progressStr, colorDef.stepColor);
});

export const workshopCardParts = {
  workshopCardTerminButton,
  workshopCardSelectionIndicator,
  workshopCardDescription,
  workshopCardDetailPageButton,
  workshopCardTags,
  workshopCapacityBar,
};
